/**
 * SEO Utilities - Enterprise-grade structured data and meta helpers
 * Supports Google, Bing, Yandex, DuckDuckGo rich results
 */
import { SupportedLanguage, SUPPORTED_LANGUAGES } from './routes';
import BRAND from '@/config/brand';

export interface SEOConfig {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
}

export interface PageSEO {
  title: string;
  description: string;
  keywords?: string[];
}

export interface BreadcrumbItem {
  name: string;
  href: string;
}

// Export SITE_URL for use in components
// CRITICAL: Always use production URL for canonical/hreflang to prevent
// preview domain from leaking into meta tags and causing duplicate issues.
export const SITE_URL = BRAND.url;

const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png?v=10`;

// ══════════════════════════════════════════════════════════════════════════════
// HREFLANG LANGUAGES — 29 supported languages for SEO sitemaps & hreflang
// ══════════════════════════════════════════════════════════════════════════════
export const SITEMAP_LANGUAGES = ['en', 'fr', 'es', 'ar', 'de', 'it', 'pt', 'nl', 'pl', 'ru', 'zh', 'ja', 'ko', 'th', 'tr', 'vi', 'el', 'sv', 'da', 'fi', 'no', 'hu', 'cs', 'ro', 'id', 'ms', 'tl', 'bn', 'hi'] as const;
export const HREFLANG_LANGUAGES = SITEMAP_LANGUAGES;

// ══════════════════════════════════════════════════════════════════════════════
// REGIONAL HREFLANG VARIANTS — Geo-targeted regional variants for physical hubs
// Maps regional BCP-47 tags to the base language code used for content routing.
// This ensures bidirectional hreflang: fr → fr-MA, es → es-ES, ar → ar-AE etc.
// ══════════════════════════════════════════════════════════════════════════════
export const REGIONAL_HREFLANG_VARIANTS: Array<{ region: string; baseLang: string }> = [
  // Morocco hubs (Casablanca, Kénitra)
  { region: 'fr-MA', baseLang: 'fr' },
  { region: 'ar-MA', baseLang: 'ar' },
  // Spain market (served remotely from Casablanca)
  { region: 'es-ES', baseLang: 'es' },
  // France (key market)
  { region: 'fr-FR', baseLang: 'fr' },
  // Belgium (bilingual market)
  { region: 'fr-BE', baseLang: 'fr' },
  { region: 'nl-BE', baseLang: 'nl' },
  // Switzerland (multilingual market)
  { region: 'fr-CH', baseLang: 'fr' },
  { region: 'de-CH', baseLang: 'de' },
  { region: 'it-CH', baseLang: 'it' },
  // Canada
  { region: 'fr-CA', baseLang: 'fr' },
  { region: 'en-CA', baseLang: 'en' },
  // UK
  { region: 'en-GB', baseLang: 'en' },
  // US
  { region: 'en-US', baseLang: 'en' },
  // Germany
  { region: 'de-DE', baseLang: 'de' },
  // LATAM
  { region: 'es-MX', baseLang: 'es' },
  { region: 'pt-BR', baseLang: 'pt' },
  // Saudi Arabia
  { region: 'ar-SA', baseLang: 'ar' }
];

// Legacy alias — kept for backward compat
export const ALL_28_LANGUAGES = SITEMAP_LANGUAGES;

/**
 * Generate hreflang links for multilingual SEO
 * All pages get hreflang for all 29 supported languages
 */
export function normalizeSeoPath(path: string): string {
  const pathOnly = path.split('?')[0].split('#')[0] || '/';
  let normalized = pathOnly.endsWith('/') && pathOnly !== '/'
    ? pathOnly.slice(0, -1)
    : pathOnly;

  let previous = '';
  while (normalized !== previous) {
    previous = normalized;
    normalized = normalized
      .replace(/^\/([a-z]{2})\/\1(?=\/|$)/i, '/$1')
      .replace(/\/([a-z]{2})\/\1(?=\/|$)/gi, '/$1')
      .replace(/\/insights(?=\/|$)/g, '/blog');
  }

  return normalized;
}

export function generateHreflangLinks(
  currentPath: string,
  _currentLang: SupportedLanguage
): Array<{ lang: string; href: string }> {
  const cleanedPath = normalizeSeoPath(currentPath);
  const segments = cleanedPath.split('/').filter(Boolean);
  const firstSegment = segments[0] as SupportedLanguage | undefined;

  const pathWithoutLang = firstSegment && SUPPORTED_LANGUAGES.includes(firstSegment)
    ? `/${segments.slice(1).join('/')}`
    : cleanedPath;

  const normalizedPath = pathWithoutLang === '/' ? '' : pathWithoutLang;
  const links: Array<{ lang: string; href: string }> = [
    {
      lang: 'x-default',
      href: `${SITE_URL}/en${normalizedPath}`,
    }
  ];

  // Base language hreflang tags (29 languages)
  for (const lang of HREFLANG_LANGUAGES) {
    links.push({
      lang,
      href: `${SITE_URL}/${lang}${normalizedPath}`,
    });
  }

  // Regional variants — bidirectional hreflang for geo-targeted markets
  // Each regional variant points to the same content as its base language
  // This ensures Google surfaces the right version for users in specific regions
  for (const { region, baseLang } of REGIONAL_HREFLANG_VARIANTS) {
    links.push({
      lang: region,
      href: `${SITE_URL}/${baseLang}${normalizedPath}`,
    });
  }

  return links;
}

/**
 * Generate canonical URL
 */
export function generateCanonical(path: string): string {
  return `${SITE_URL}${normalizeSeoPath(path)}`;
}

/**
 * Generate Open Graph URL
 */
export function generateOGUrl(path: string): string {
  return `${SITE_URL}${path}`;
}

/**
 * Generate dynamic OG image URL via the backend image endpoint.
 */
export function generateDynamicOGImage(params: {
  title: string;
  subtitle?: string;
  lang?: string;
  path?: string;
}): string {
  const search = new URLSearchParams();
  search.set('title', params.title.slice(0, 80));
  if (params.subtitle) search.set('subtitle', params.subtitle.slice(0, 120));
  if (params.lang) search.set('lang', params.lang);
  if (params.path) search.set('path', normalizeSeoPath(params.path));
  return `${SITE_URL}/api/og?${search.toString()}`;
}

/**
 * Get default OG image
 */
export function getDefaultOGImage(): string {
  return DEFAULT_OG_IMAGE;
}

/**
 * Organization JSON-LD Schema
 * Enhanced with all required properties for rich results
 */
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: BRAND.name,
    alternateName: ['bpo-contact'],
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/favicon.svg`,
      contentUrl: `${SITE_URL}/favicon.svg`,
      width: 512,
      height: 370,
    },
    image: `${SITE_URL}/og-image.png?v=10`,
    description: 'Multilingual contact center — customer service, technical support and back-office, headquartered in Casablanca with production in Kénitra, Morocco.',
    slogan: 'Excellence in Human Connection. Power in Digital Innovation.',
    naics: '561422', // Telemarketing Bureaus and Other Contact Centers
    isicV4: '8220', // Activities of call centres
    legalName: 'BPO Contact',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Casablanca',
      addressCountry: 'MA',
    },
    location: [
      { '@type': 'Place', name: 'BPO Contact — head office', address: { '@type': 'PostalAddress', addressLocality: 'Casablanca', addressCountry: 'MA' } },
      { '@type': 'Place', name: 'BPO Contact — production site', address: { '@type': 'PostalAddress', addressLocality: 'Kénitra', addressCountry: 'MA' } },
    ],
    areaServed: [
      { '@type': 'Country', name: 'Morocco' },
      { '@type': 'Country', name: 'Spain' },
      { '@type': 'Place', name: 'Europe' },
      { '@type': 'Place', name: 'GCC' }
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: BRAND.phone.support,
        contactType: 'customer service',
        email: BRAND.email.contact,
        areaServed: ['World'],
      },
      {
        '@type': 'ContactPoint',
        telephone: BRAND.phone.main,
        contactType: 'sales',
        email: BRAND.email.sales,
      },
      {
        '@type': 'ContactPoint',
        telephone: BRAND.phone.morocco,
        contactType: 'sales',
        areaServed: ['Africa', 'Europe'],
      }
    ],
    sameAs: [
      BRAND.social.twitter,
      BRAND.social.facebook,
      BRAND.social.instagram
    ],
    knowsAbout: [
      'BPO Services', 'Business Process Outsourcing', 'Call Center Operations', 'Contact Center Management',
      'Customer Experience (CX)', 'Customer Support Outsourcing', 'Customer Service Management',
      'Inbound Call Center', 'Outbound Call Center', 'Blended Call Center',
      'Technical Support Outsourcing', 'IT Help Desk',
      'Tele-Sales', 'Telemarketing', 'Lead Generation', 'Appointment Setting', 'SDR Outsourcing',
      'Customer Retention', 'Win-Back Campaigns', 'Churn Prevention', 'Loyalty Programs',
      'Content Moderation', 'Trust and Safety', 'UGC Review', 'Platform Safety',
      'Back Office Outsourcing', 'Data Entry', 'Document Processing', 'Order Management',
      'Billing Support', 'Collections', 'Accounts Receivable',
      'Quality Assurance', 'Call Monitoring', 'Speech Analytics',
      'WhatsApp Business Support', 'Live Chat Support', 'Email Support', 'Social Media Customer Care',
      'Multilingual Customer Service', 'French Call Center', 'Arabic Call Center', 'Spanish Call Center',
      'German Call Center', 'Italian Call Center', 'Portuguese Call Center', 'Dutch Call Center',
      'Web Development', 'E-commerce Development', 'Enterprise Portal Development',
      'Mobile App Development', 'iOS Development', 'Android Development', 'React Native', 'Flutter',
      'SaaS Development', 'SaaS Platform Engineering', 'MVP Development',
      'API Development', 'REST API', 'GraphQL', 'System Integration',
      'UI/UX Design', 'User Experience Design', 'Design Systems', 'Figma',
      'Cloud Solutions', 'AWS', 'Azure', 'Google Cloud', 'DevOps', 'CI/CD', 'Docker', 'Kubernetes',
      'AI Solutions', 'Machine Learning', 'Chatbot Development', 'NLP', 'RPA', 'ChatGPT Integration',
      'Cybersecurity', 'Penetration Testing', 'Application Security', 'OWASP',
      'Data Engineering', 'Business Intelligence', 'ETL Pipelines', 'Tableau', 'Power BI',
      'Growth Marketing', 'SEO Services', 'Google Ads', 'Performance Marketing',
      'Nearshore Outsourcing', 'Offshore Outsourcing', 'BPO Morocco',
      'Centre d\'appel', 'Externalisation', 'Service client externalisé', 'Développement web',
      'مركز اتصال', 'تعهيد خدمة العملاء', 'تطوير ويب',
      'Centro de llamadas', 'Externalización', 'Callcenter', 'Kundenservice',
      'Software Testing', 'QA Automation', 'Managed IT Services',
      'DevSecOps', 'Site Reliability Engineering', 'Omnichannel Support',
      'CRM Integration', 'Salesforce', 'Zendesk', 'HubSpot',
    ],
  };
}

/**
 * WebSite JSON-LD Schema
 */
export function generateWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: BRAND.name,
    alternateName: ['bpo-contact'],
    url: SITE_URL,
    description: 'Enterprise BPO & Digital Factory',
    publisher: { '@id': `${SITE_URL}/#organization` },
    inLanguage: SUPPORTED_LANGUAGES as unknown as string[],
    potentialAction: {
      '@type': 'CommunicateAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/en/contact`,
      },
      name: 'Request a Quote',
    },
  };
}

/**
 * LocalBusiness JSON-LD Schema (for each location)
 */
export function generateLocalBusinessSchema(location: 'morocco' | 'spain' | 'uae') {
  const locations = {
    uae: {
      name: `BPO Contact — Serving the UAE and GCC remotely from Casablanca`,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Casablanca',
        addressCountry: 'MA',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 33.5731,
        longitude: -7.5898,
      },
      telephone: BRAND.phone.main,
      areaServed: ['UAE', 'Middle East', 'GCC', 'Asia-Pacific'],
    },
    morocco: {
      name: `BPO Contact Morocco - Casablanca Delivery Center`,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Casablanca',
        addressCountry: 'MA',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 33.5731,
        longitude: -7.5898,
      },
      telephone: BRAND.phone.morocco,
      areaServed: ['Morocco', 'France', 'Europe', 'Africa'],
    },
    spain: {
      name: `BPO Contact - Serving Spain Remotely from Casablanca`,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Casablanca',
        addressCountry: 'MA',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 33.5731,
        longitude: -7.5898,
      },
      telephone: BRAND.phone.main,
      areaServed: ['Spain', 'Latin America', 'Europe'],
    },
  };
  
  const loc = locations[location];
  
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': `${SITE_URL}/locations/${location}#localbusiness`,
    name: loc.name,
    image: `${SITE_URL}/og-image.png?v=10`,
    url: `${SITE_URL}/en/locations/${location}`,
    telephone: loc.telephone,
    email: BRAND.email.contact,
    address: loc.address,
    geo: loc.geo,
    areaServed: loc.areaServed,
    parentOrganization: { '@id': `${SITE_URL}/#organization` },
  };
}

/**
 * Service JSON-LD Schema
 */
export function generateServiceSchema(
  serviceName: string, 
  description: string,
  category: 'bpo' | 'digital' = 'bpo',
  slug?: string,
  language: string = 'en'
) {
  const catPath = category === 'bpo' ? 'bpo' : 'digital-studio';
  const serviceUrl = slug ? `${SITE_URL}/${language}/services/${catPath}/${slug}` : `${SITE_URL}/${language}`;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${serviceUrl}#service`,
    serviceType: serviceName,
    name: serviceName,
    description,
    url: serviceUrl,
    provider: { '@id': `${SITE_URL}/#organization` },
    areaServed: [
      { '@type': 'Continent', name: 'Europe' },
      { '@type': 'Continent', name: 'North America' },
      { '@type': 'Continent', name: 'Africa' },
      { '@type': 'Continent', name: 'Asia' },
      { '@type': 'Place', name: 'Middle East' },
      { '@type': 'Place', name: 'Latin America' }
    ],
    inLanguage: language,
    serviceOutput: category === 'bpo' 
      ? 'Customer service, technical support and back-office operations'
      : 'Custom software solutions, scalable web applications, mobile apps',
    termsOfService: `${SITE_URL}/${language}/legal/terms`,
  };
}

/**
 * FAQ JSON-LD Schema
 */
export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * BreadcrumbList JSON-LD Schema
 */
export function generateBreadcrumbSchema(breadcrumbs: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.href.startsWith('http') ? item.href : `${SITE_URL}${item.href}`,
    })),
  };
}

/**
 * Article/BlogPosting JSON-LD Schema
 */
export function generateArticleSchema(article: {
  title: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  image?: string;
  url: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image || DEFAULT_OG_IMAGE,
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: {
      '@type': 'Organization',
      name: article.author || BRAND.name,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: BRAND.name,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/favicon.svg`,
        contentUrl: `${SITE_URL}/favicon.svg`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': article.url,
    },
  };
}

/**
 * HowTo JSON-LD Schema (for process/timeline sections)
 */
export function generateHowToSchema(
  name: string,
  description: string,
  steps: Array<{ name: string; text: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  };
}

/**
 * SiteNavigationElement JSON-LD (helps Google understand site architecture)
 */
export function generateSiteNavigationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${SITE_URL}/#navigation`,
    name: 'Main Navigation',
    itemListElement: [
      { '@type': 'SiteNavigationElement', position: 1, name: 'Services', url: `${SITE_URL}/en/services` },
      { '@type': 'SiteNavigationElement', position: 2, name: 'BPO Contact Center', url: `${SITE_URL}/en/services/bpo` },
      { '@type': 'SiteNavigationElement', position: 3, name: 'Digital Studio', url: `${SITE_URL}/en/services/digital-studio` },
      { '@type': 'SiteNavigationElement', position: 4, name: 'Solutions', url: `${SITE_URL}/en/solutions` },
      { '@type': 'SiteNavigationElement', position: 5, name: 'Why Morocco', url: `${SITE_URL}/en/why-morocco` },
      { '@type': 'SiteNavigationElement', position: 6, name: 'About', url: `${SITE_URL}/en/about` },
      { '@type': 'SiteNavigationElement', position: 7, name: 'Pricing', url: `${SITE_URL}/en/pricing` },
      { '@type': 'SiteNavigationElement', position: 8, name: 'Case Studies', url: `${SITE_URL}/en/case-studies` },
      { '@type': 'SiteNavigationElement', position: 9, name: 'Blog', url: `${SITE_URL}/en/blog` },
      { '@type': 'SiteNavigationElement', position: 10, name: 'Contact', url: `${SITE_URL}/en/contact` },
      { '@type': 'SiteNavigationElement', position: 11, name: 'Industries', url: `${SITE_URL}/en/industries` },
      { '@type': 'SiteNavigationElement', position: 12, name: 'ROI Calculator', url: `${SITE_URL}/en/roi-calculator` },
      { '@type': 'SiteNavigationElement', position: 13, name: 'Careers', url: `${SITE_URL}/en/careers` }
    ],
  };
}

/**
 * Speakable JSON-LD Schema (for Google Assistant / voice search / AI citations)
 * Marks content as citable by voice assistants and AI search engines
 */
export function generateSpeakableSchema(
  pageUrl?: string,
  cssSelectors: string[] = ['h1', '.hero-description', '.faq-answer', '.page-summary']
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': pageUrl ? `${pageUrl}#speakable` : `${SITE_URL}#speakable`,
    url: pageUrl || SITE_URL,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: cssSelectors,
    },
  };
}

/**
 * ItemList JSON-LD Schema (for rich snippets on listing pages)
 */
export function generateServiceListSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${SITE_URL}/en/services#servicelist`,
    name: 'BPO Contact Services',
    description: 'Complete catalog of BPO and digital development services',
    numberOfItems: 12,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inbound Customer Support', url: `${SITE_URL}/en/services/bpo/inbound-customer-care` },
      { '@type': 'ListItem', position: 2, name: 'Outbound Sales & Telemarketing', url: `${SITE_URL}/en/services/bpo/outbound-sales` },
      { '@type': 'ListItem', position: 3, name: 'Technical Support & Helpdesk', url: `${SITE_URL}/en/services/bpo/technical-support` },
      { '@type': 'ListItem', position: 4, name: 'Multilingual Support (multilingual)', url: `${SITE_URL}/en/services/bpo/multilingual-support` },
      { '@type': 'ListItem', position: 5, name: 'Live Chat & Email Support', url: `${SITE_URL}/en/services/bpo/live-chat-messaging` },
      { '@type': 'ListItem', position: 6, name: 'Content Moderation & Trust Safety', url: `${SITE_URL}/en/services/bpo/trust-safety-compliance` },
      { '@type': 'ListItem', position: 7, name: 'Custom SaaS Development', url: `${SITE_URL}/en/services/digital-studio/custom-saas` },
      { '@type': 'ListItem', position: 8, name: 'Mobile App Development', url: `${SITE_URL}/en/services/digital-studio/mobile-apps` },
      { '@type': 'ListItem', position: 9, name: 'E-commerce Development', url: `${SITE_URL}/en/services/digital-studio/ecommerce` },
      { '@type': 'ListItem', position: 10, name: 'Cloud & DevOps', url: `${SITE_URL}/en/services/digital-studio/cloud-devops` },
      { '@type': 'ListItem', position: 11, name: 'AI & Emerging Tech', url: `${SITE_URL}/en/services/digital-studio/emerging-tech-ai` },
      { '@type': 'ListItem', position: 12, name: 'Cybersecurity & AppSec', url: `${SITE_URL}/en/services/digital-studio/cybersecurity-appsec` }
    ],
  };
}

/**
 * JobPosting JSON-LD Schema (for careers page)
 */
export function generateJobPostingSchema(job: {
  title: string;
  description: string;
  location: string;
  employmentType: 'FULL_TIME' | 'PART_TIME' | 'CONTRACT';
  datePosted: string;
  validThrough?: string;
  salaryMin?: number;
  salaryCurrency?: string;
  salaryUnit?: string;
  remote?: boolean;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: job.description,
    datePosted: job.datePosted,
    validThrough: job.validThrough || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
    employmentType: job.employmentType,
    hiringOrganization: { '@id': `${SITE_URL}/#organization` },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: job.location,
      },
    },
    ...(job.remote && {
      jobLocationType: 'TELECOMMUTE',
      applicantLocationRequirements: { '@type': 'Country', name: 'Worldwide' },
    }),
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: job.salaryCurrency || 'USD',
      value: {
        '@type': 'QuantitativeValue',
        ...(job.salaryMin && { minValue: job.salaryMin }),
        unitText: job.salaryUnit || 'MONTH',
      },
    },
  };
}

/**
 * ProfilePage JSON-LD Schema (E-E-A-T signal — Google's Experience, Expertise, Authority, Trust)
 * Helps Google assess the credibility of the organization behind the content
 */
export function generateProfilePageSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': `${SITE_URL}/en/about#profilepage`,
    mainEntity: { '@id': `${SITE_URL}/#organization` },
    dateModified: new Date().toISOString().split('T')[0],
  };
}

/**
 * CollectionPage JSON-LD Schema (for listing/hub pages — triggers carousel rich results)
 */
export function generateCollectionPageSchema(
  name: string,
  description: string,
  url: string,
  items: Array<{ name: string; url: string; description?: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${url}#collectionpage`,
    name,
    description,
    url: url.startsWith('http') ? url : `${SITE_URL}${url}`,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: items.length,
      itemListElement: items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
        url: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
        ...(item.description ? { description: item.description } : {}),
      })),
    },
  };
}

/**
 * SpecialAnnouncement JSON-LD (for promotions, limited offers — triggers rich banner in SERP)
 */
export function generateSpecialAnnouncementSchema(announcement: {
  name: string;
  text: string;
  datePosted?: string;
  expires?: string;
  url?: string;
  category?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SpecialAnnouncement',
    name: announcement.name,
    text: announcement.text,
    datePosted: announcement.datePosted || new Date().toISOString().split('T')[0],
    expires: announcement.expires || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    url: announcement.url || `${SITE_URL}/en/pricing`,
    category: announcement.category || 'https://www.wikidata.org/wiki/Q7188',
    announcementLocation: {
      '@type': 'VirtualLocation',
      url: `${SITE_URL}/en/contact`,
    },
  };
}

/**
 * Event JSON-LD Schema (for webinars, demos — triggers event rich results)
 */
export function generateEventSchema(event: {
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  url?: string;
  isOnline?: boolean;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.name,
    description: event.description,
    startDate: event.startDate,
    endDate: event.endDate || event.startDate,
    url: event.url || `${SITE_URL}/en/contact`,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: event.isOnline !== false
      ? 'https://schema.org/OnlineEventAttendanceMode'
      : 'https://schema.org/OfflineEventAttendanceMode',
    location: event.isOnline !== false
      ? { '@type': 'VirtualLocation', url: event.url || `${SITE_URL}/en/contact` }
      : { '@type': 'Place', name: 'BPO Contact HQ', address: { '@type': 'PostalAddress', addressLocality: 'Casablanca', addressCountry: 'MA' } },
    organizer: { '@id': `${SITE_URL}/#organization` },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: event.url || `${SITE_URL}/en/contact`,
    },
  };
}

/**
 * VideoObject JSON-LD Schema (for embedded videos — triggers video rich results)
 */
export function generateVideoSchema(video: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string; // ISO 8601 e.g. PT2M30S
  contentUrl?: string;
  embedUrl?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.name,
    description: video.description,
    thumbnailUrl: video.thumbnailUrl,
    uploadDate: video.uploadDate,
    duration: video.duration || 'PT3M',
    contentUrl: video.contentUrl,
    embedUrl: video.embedUrl,
    publisher: { '@id': `${SITE_URL}/#organization` },
  };
}

/**
 * SoftwareApplication JSON-LD (for SaaS product pages — triggers software rich results)
 */
export function generateSoftwareApplicationSchema(app: {
  name: string;
  description: string;
  applicationCategory?: string;
  operatingSystem?: string;
  url?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: app.name,
    description: app.description,
    applicationCategory: app.applicationCategory || 'BusinessApplication',
    operatingSystem: app.operatingSystem || 'Web',
    url: app.url || `${SITE_URL}/en/services/digital-studio/custom-saas`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    provider: { '@id': `${SITE_URL}/#organization` },
  };
}
